import { InteractionStatus } from '@azure/msal-browser';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-common';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import type { SeverityLevel } from '@sentry/react';
import { jwtDecode } from 'jwt-decode';
import { useEffect, useRef } from 'react';

import { authRequest, authRequestForSilentAndPopupFlows } from '@shared/providers/AzureAuthProvider/authConfig.ts';
import { parseCountryCodeFromToken, UserCountryCode } from '@shared/types/countryCodes.ts';

export interface UseAuth {
  getPersonalInfo: () => Promise<string | null>;
  getRoleId: () => Promise<string | null>;
  logout: () => void;
  getUserId: () => Promise<string | null>;
  getUserCountryCode: () => Promise<UserCountryCode | null>;
}

interface RequestToken {
  account?: AccountInfo;
  scopes: string[];
  [key: string]: unknown;
}

interface CanProceedWithSilentFlowGuard {
  inProgress: string;
  isAuthenticated: boolean;
  capturedMessageText: string;
}

// Sentry is loaded on first use: a static import pulls the whole SDK into every module (and test) using this hook
const captureMessage = (message: string) => {
  void import('@sentry/react').then((sentry) => sentry.captureMessage(message));
};

const captureException = (exception: unknown, context: { level: SeverityLevel }) => {
  void import('@sentry/react').then((sentry) => sentry.captureException(exception, context));
};

export const EXTERNAL_USER_ROLE_IDS = {
  DIRECT_CLIENT_ID: 'externalDirectClient',
  AGENCY_ID: 'externalAgency',
};

const TOKEN_EXTERNAL_USER_ROLE = {
  DIRECT_CLIENT: 'DIRECT_CLIENT',
  AGENCY: 'AGENCY',
};

function canProceedWithSilentFlow({ inProgress, isAuthenticated, capturedMessageText }: CanProceedWithSilentFlowGuard) {
  if (inProgress !== InteractionStatus.None && !isAuthenticated) {
    captureMessage(`${capturedMessageText} | isAuthenticated: ${isAuthenticated}, inProgress: ${inProgress}`);

    return false;
  }

  return true;
}

export function useAuth(): UseAuth {
  const storedAccount = useRef<AccountInfo>();
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const accountsFound = accounts && accounts.length > 0;

  useEffect(() => {
    const activeAccount = accountsFound ? accounts[0] : undefined;

    if (!activeAccount) {
      storedAccount.current = undefined;
      return;
    }

    if (storedAccount.current?.homeAccountId !== activeAccount.homeAccountId) {
      storedAccount.current = activeAccount;
    }
  }, [accounts, accountsFound]);

  const logout = async () => {
    const canProceed = canProceedWithSilentFlow({
      inProgress,
      isAuthenticated,
      capturedMessageText: `logout , trying to access instance.logoutRedirect()`,
    });

    if (!canProceed) {
      return null;
    }

    await instance.logoutRedirect().catch((error) => {
      captureMessage('Failed to logout user.');
      captureException(inProgress, { level: 'info' });
      captureException(error, { level: 'error' });
    });
  };

  async function fallbackProcedure(error: unknown, requestToken: RequestToken) {
    if (error instanceof InteractionRequiredAuthError && inProgress === InteractionStatus.None && !isAuthenticated) {
      try {
        await instance.acquireTokenRedirect(requestToken);
      } catch (redirectError) {
        captureMessage(`Failed to acquire token redirect. Attempting to logout...`);
        captureException(redirectError, { level: 'error' });

        await logout();
      }
    } else {
      captureMessage(`Failed to acquire token silently. Attempting to logout...`);
      captureException(error, { level: 'error' });

      await logout();
    }
  }

  const getPersonalInfo = async () => {
    const accessTokenRequest = { ...authRequest, account: storedAccount.current };
    const accessTokenSilentRequest = { ...authRequestForSilentAndPopupFlows, account: storedAccount.current };

    const canProceed = canProceedWithSilentFlow({
      inProgress,
      isAuthenticated,
      capturedMessageText: `getPersonalInfo, failed to acquire token silently.`,
    });

    if (!canProceed) {
      return null;
    }

    try {
      const {
        account: { name },
      } = await instance.acquireTokenSilent(accessTokenSilentRequest);

      return name || '';
    } catch (error) {
      await fallbackProcedure(error, accessTokenRequest);
    }

    return null;
  };

  const getUserId = async (): Promise<string | null> => {
    const accessTokenRequest = { ...authRequest, account: storedAccount.current };
    const accessTokenSilentRequest = { ...authRequestForSilentAndPopupFlows, account: storedAccount.current };

    const canProceed = canProceedWithSilentFlow({
      inProgress,
      isAuthenticated,
      capturedMessageText: `getUserId, failed to acquire token silently.`,
    });

    if (!canProceed) {
      return null;
    }

    try {
      const { idTokenClaims } = await instance.acquireTokenSilent(accessTokenSilentRequest);

      if (!idTokenClaims) {
        return null;
      }

      return 'oid' in idTokenClaims && typeof idTokenClaims.oid === 'string' ? idTokenClaims.oid : null;
    } catch (error) {
      await fallbackProcedure(error, accessTokenRequest);
    }

    return null;
  };

  const getRoleId = async (): Promise<string | null> => {
    const accessTokenRequest = { ...authRequest, account: storedAccount.current };
    const accessTokenSilentRequest = { ...authRequestForSilentAndPopupFlows, account: storedAccount.current };

    const canProceed = canProceedWithSilentFlow({
      inProgress,
      isAuthenticated,
      capturedMessageText: `getRoleId, failed to acquire token silently.`,
    });

    if (!canProceed) {
      return null;
    }

    try {
      const { idTokenClaims } = await instance.acquireTokenSilent(accessTokenSilentRequest);

      if (!idTokenClaims) {
        return null;
      }

      if ('idp_id_token' in idTokenClaims && typeof idTokenClaims.idp_id_token === 'string') {
        const decodedIdpIdToken = jwtDecode(idTokenClaims.idp_id_token);

        const roleId =
          'groups' in decodedIdpIdToken && Array.isArray(decodedIdpIdToken.groups) ? decodedIdpIdToken.groups[0] : null;

        return roleId;
      }

      const isExternalUserTypeData =
        'extension_clientType' in idTokenClaims && typeof idTokenClaims.extension_clientType === 'string';

      if (isExternalUserTypeData && idTokenClaims.extension_clientType === TOKEN_EXTERNAL_USER_ROLE.DIRECT_CLIENT) {
        return EXTERNAL_USER_ROLE_IDS.DIRECT_CLIENT_ID;
      }

      if (isExternalUserTypeData && idTokenClaims.extension_clientType === TOKEN_EXTERNAL_USER_ROLE.AGENCY) {
        return EXTERNAL_USER_ROLE_IDS.AGENCY_ID;
      }
    } catch (error) {
      await fallbackProcedure(error, accessTokenRequest);
    }

    return null;
  };

  const getUserCountryCode = async (): Promise<UserCountryCode | null> => {
    const accessTokenRequest = { ...authRequest, account: storedAccount.current };
    const accessTokenSilentRequest = { ...authRequestForSilentAndPopupFlows, account: storedAccount.current };

    const canProceed = canProceedWithSilentFlow({
      inProgress,
      isAuthenticated,
      capturedMessageText: `getUserCountryCode, failed to acquire token silently.`,
    });

    if (!canProceed) {
      return null;
    }

    try {
      const { idTokenClaims } = await instance.acquireTokenSilent(accessTokenSilentRequest);

      if (!idTokenClaims) {
        return null;
      }

      const isExternalUserType =
        'extension_clientType' in idTokenClaims && typeof idTokenClaims.extension_clientType === 'string';

      if ('extension_country' in idTokenClaims && isExternalUserType) {
        return parseCountryCodeFromToken(idTokenClaims.extension_country);
      }
    } catch (error) {
      await fallbackProcedure(error, accessTokenRequest);
    }

    return null;
  };

  return { logout, getRoleId, getPersonalInfo, getUserId, getUserCountryCode };
}
